
import { Vue, Options, prop } from "vue-class-component";
import { maxLengthWeight } from "../../../../create-booking-v2/components/modules";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { HeavyWeightSurchargeController } from "@/app/ui/controllers/HeavyWeightSurchargeController";
import {
  maxKg,
  volumeWeightForProduct,
  totalGrossWeightSikat,
  totalGrossWeightNonSikat,
  gapValidationCHWeightTokped
} from "../../../../booking-utils";
import { parseZodError, validationCurrentIndex } from "../../../utils";
import {
  perItemSchema,
  totalGrossWeightSchema,
  totalVolumeWeightSchema
} from "./validation";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import { commaDecimalToDecimal, formatByCountry, formatInputByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import { HeavyWeightSurchargeDetailMinimum } from "@/domain/entities/HeavyWeightSurcharge";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";
import { CreateBookingV3Controller } from "@/app/ui/controllers/CreateBookingV3Controller";
import { cloneDeep } from "lodash";
import { isShipmentCa } from "@/app/ui/modules/receipt-v2/module";
import { ProductController } from "@/app/ui/controllers/ProductController";
import { LocationController } from "@/app/ui/controllers/LocationController";

class Props {
  tempPieces = prop<any>({
    default: []
  });
  pieces = prop<any>({
    required: true
  });
  form = prop<any>({
    required: true
  });
  showWarningHwsProp = prop<boolean>({
    required: true
  });
  isCampaign = prop<boolean>({
    required: true
  });
  errorEmbargo = prop<any>({
    required: true
  });
  maxPieces = prop<number>({
    required: true
  });
  isInterpackLanePending = prop<boolean>({
    type: Boolean,
    default: false
  });
  currentIndex = prop<number>({
    required: true
  });
  pieceIndex = prop<number>({
    required: true
  });
  configGrossWeight = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configDimension = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAdjustPiece = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  hwsMinimumData = prop<HeavyWeightSurchargeDetailMinimum>({
    default: new HeavyWeightSurchargeDetailMinimum(),
    required: true
  });
  disabledWeight = prop<boolean>({
    default: false
  });
  disabledDimension = prop<boolean>({
    default: false
  });
  configAdjustPieceAdd = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAdjustPieceRemove = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  listKgDefault = prop<any>({
    default: []
  });
  isEditForm = prop<boolean>({
    default: false
  });
  isShipment = prop<boolean>({
    default: false
  });
  dataRuleShipmentBooking = prop<any>({
    required: true
  });
  eligibleEditSize = prop<boolean>({
    default: false
  });
  isBookingShipment = prop<boolean>({
    default: false
  });
  totalChargeableWeight = prop<number>({
    default: 0,
    type: Number
  });
}

@Options({
  emits: ["update:pieces", "update,currentIndex"],
  watch: {
    currentIndex: {
      handler: function() {
        if (!this.totalPieces.length) {
          this.addKGs();
        }
      }
    },
    "form.productName"(productNameValue: string) {
      this.totalPieces.forEach((el: any, i: number) => {
        this.calculationVolumeWeight(i);
        this.setOnValidationHws(el.grossWeight, i);
      });
      if (productNameValue) {
        this.tempProductName = productNameValue;
      }
    },
    "form.commodity"() {
      this.totalPieces.forEach((el: any, i: number) => {
        this.calculationVolumeWeight(i);
        this.setOnValidationHws(el.grossWeight, i);
      });
    },
    totalPieces(val) {
      val.forEach((el: any, i: number) => {
        this.setCalculateVolumeWeight(i);
        this.setOnValidationHws(el.grossWeight, i);
      });
    },
    totalChargeableWeight() {
      this.totalPieces.forEach((_piece: any, index: number) => {
        this.onValidateGrossWeight(index)
        this.onValidateGrossWeightEdit(index)
      });
    }
  }
})
class WeightKoli extends Vue.with(Props) {
  isHoverDelete = false;
  tempIndexToRemove = -1;
  indexVolume = 0;
  isMaxLengthGrossWeight: any = null;
  get countryCode() {
    return ProductController.countryCode;
  }
  get volumetricDivisorMap() {
    return ProductController.volumetricDivisorMap;
  }
  get division() {
    return volumeWeightForProduct(
      this.form.productName,
      this.volumetricDivisorMap,
      this.countryCode
    );
  }
  error = {
    totalGrossWeight: "",
    totalVolumeWeight: ""
  };
  tempProductName = "";

  async mounted() {
    this.piecesTemporary = cloneDeep(this.pieces);

    if (!this.totalPieces.length) {
      this.addKGs(true);
    }

    if (this.isEditForm) {
      this.totalPieces.forEach((el: any, i: number) => {
        this.setCalculateVolumeWeight(i);
        this.setOnValidationHws(el.grossWeight, i);
      });
    }

    this.totalChargeableWeightKeeped = cloneDeep(this.totalChargeableWeight);
  }
  get getRuleGrossWeightDisabled() {
    return (
      this.configGrossWeight.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      this.disabledWeight
    );
  }

  get getRuleDimensionDisabled() {
    return (
      this.configDimension.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      this.disabledDimension
    );
  }

  get getRuleAdjustPiece() {
    const isNoShow = this.configAdjustPiece.rule.includes(
      ConfigValidationForm.NON_SHOW
    );
    const isNonEditable = this.configAdjustPiece.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
    return isNoShow || isNonEditable;
  }

  get accountIsNonForeign() {
    if (this.interpackLaneEligible) return true;
    return !AccountController.accountData.accountIsForeign;
  }

  get accountCountryCode() {
    return AccountController.accountData.countryCode?.toUpperCase();
  }

  piecesTemporary: any[] = [];
  get totalPieces() {
    return this.pieces;
  }

  set totalPieces(value) {
    this.$emit("update:pieces", value);
  }

  formatFloat(value: string) {
    // Use foundation country-aware formatting instead of force-replacing
    const formatted = formatInputByCountry(value, {
      type: "weight",
      country: this.accountCountryCode
    });
    this.isMaxLengthGrossWeight = maxLengthWeight(
      formatted,
      this.accountCountryCode
    );
    return formatted;
  }

  calculationVolumeWeight(index: number) {
    this.indexVolume = index;

    const piece = this.totalPieces[index];
    if (!piece) return;

    const length = piece.dimension?.length ?? 3;
    const width = piece.dimension?.width ?? 20;
    const height = piece.dimension?.height ?? 10;

    const divisor = volumeWeightForProduct(
      this.form.productName,
      this.volumetricDivisorMap,
      this.countryCode
    );

    const weight =
      (length * width * height) /
      divisor;

    this.totalPieces[index].volumeWeight =
      formatByCountry(weight, {
        type: "weight",
        country: this.accountCountryCode
      });

    // Format volume weight display by country instead of force-replacing
    this.totalPieces[index].convertVolumeWeight = formatByCountry(
      this.parseNumber(this.totalPieces[index].volumeWeight),
      {
        type: "weight",
        country: this.accountCountryCode
      }
    );
    this.onValidateVolumeWeight(index);
  }

  setCalculateVolumeWeight(index: number) {
    // Format volume weight display by country instead of force-replacing
    this.totalPieces[index].convertVolumeWeight = formatByCountry(
      this.parseNumber(this.totalPieces[index].volumeWeight), 
      { 
        type: "weight", 
        country: this.accountCountryCode
      }
    );
  }

  get isMaxAddKgs() {
    return this.totalPieces.length < this.maxPieces;
  }

  removeKGs(index: number) {
    this.isHoverDelete = false;
    this.tempIndexToRemove = index;
    if (this.tempIndexToRemove > -1) {
      this.totalPieces.splice(this.tempIndexToRemove, 1);
      this.tempIndexToRemove = -1;
    }

    this.$emit("onSetErrorHws");
  }

  get defaultLength() {
    return 3;
  }

  get defaultWidth() {
    return 20;
  }

  get defaultHeight() {
    return 10;
  }

  async addKGs(firstLanding = false) {
    const weight =
      (this.defaultLength * this.defaultWidth * this.defaultHeight) /
      this.division;

    this.totalPieces.push({
      grossWeight: "0",
      dimension: {
        length: this.defaultLength,
        width: this.defaultWidth,
        height: this.defaultHeight
      },
      volumeWeight: weight,
      convertVolumeWeight: formatByCountry(weight, {
        type: "weight",
        country: this.accountCountryCode
      }),
      errorHws: false,
      errorGrossWeight: "",
      errorVolumeWeight: ""
    });

    if (firstLanding) return;

    await this.$nextTick();
    (this.$refs[
      `inputGrossWeight${this.totalPieces.length - 1}`
    ] as any).$refs.inputForm.focus();
  }

  get showWarningHws() {
    return this.showWarningHwsProp;
  }

  set showWarningHws(value) {
    this.$emit("update:showWarningHwsProp", value);
  }

  get configHeavyWeightMinimum() {
    return this.hwsMinimumData;
  }

  async setOnValidationHws(value = "", index = 0) {
    await this.$nextTick();
    this.showWarningHws =
      this.configHeavyWeightMinimum?.minimumExist &&
      this.totalPieces.length < 2 &&
      this.parseNumber(value) >=
        this.configHeavyWeightMinimum?.heavyWeightMinimum;
    this.totalPieces[index].errorHws =
      this.configHeavyWeightMinimum?.minimumExist &&
      this.totalPieces.length > 1 &&
      this.parseNumber(value) >=
        this.configHeavyWeightMinimum?.heavyWeightMinimum;
  }

  get arrGrossWeight() {
    return this.totalPieces.map((key: any) => this.parseNumber(key.grossWeight.toString()));
  }

  get arrVolumeWeight() {
    return this.totalPieces.map((key: any) => this.parseNumber(key.volumeWeight.toString()));
  }

  private sum(values: number[]) {
    return values.reduce((sum, value) => sum + value, 0);
  }

  get totalGrossWeight() {
    return formatByCountry(this.sum(this.arrGrossWeight), {
      type: "weight",
      country: this.accountCountryCode
    });
  }

  get totalVolumeWeight() {
    return formatByCountry(this.sum(this.arrVolumeWeight), {
      type: "weight",
      country: this.accountCountryCode
    });
  }

  get validateTotalGrossWeight(): boolean {
    const total = this.parseNumber(this.totalGrossWeight);
    return total > maxKg;
  }

  get validateTotalVolumeWeight(): boolean {
    const total = this.parseNumber(this.totalVolumeWeight);
    return total > maxKg;
  }

  get isGrossWeight() {
    const x = this.parseNumber(this.totalGrossWeight);
    const y = this.parseNumber(this.totalVolumeWeight);
    return x > y;
  }

  async onChangeGrossWeight(index: number) {
    await this.$nextTick();
    this.onValidateGrossWeight(index);
    this.onValidateGrossWeightEdit(index);
    this.$emit("onUpdateEmbargoStatusFail");
    this.$emit("updateGrossWeight", index);
  }

  onChangeVolumeWeight() {
    this.$emit("onUpdateEmbargoStatusFail");
  }

  get isPosAccount() {
    return AccountController.accountData.isPosAccount;
  }

  validationErrorAdjustWeight(index: number) {
    return (
      Number(this.totalPieces[index].grossWeight.replace(",", ".")) <
        this.tempPieces[index]?.grossWeight &&
      !this.disabledWeight &&
      this.isPosAccount
    );
  }

  get errorProhibitedChangeWeightShipmentCA() {
    if (this.isShipmentCA) {
      return this.totalChargeableWeight < this.totalChargeableWeightKeeped;
    }

    return false;
  }
  onValidateGrossWeightEdit(index: number) {
    if (!this.tempPieces.length) return;
    if (
      !this.totalPieces[index].errorGrossWeight &&
      this.validationErrorAdjustWeight(index)
    ) {
      this.totalPieces[index].errorGrossWeight = this.$t(
        "bookingV3.shipment.weightPieces.error.prohibitedChangeWeight"
      );
    }
  }

  onValidateGrossWeight(index: number) {
    const result = perItemSchema.safeParse({
      grossWeight: this.parseNumber(this.totalPieces[index].grossWeight),
      volumeWeight: this.parseNumber(this.totalPieces[index].volumeWeight)
    });

    this.totalPieces[index].errorGrossWeight = result.success
      ? ""
      : this.$t(result.error.format().grossWeight?._errors?.[0] || "");

    if (!this.totalPieces[index].errorGrossWeight) {
      this.validateGrossWeightEditShipmentCA(index);
    }
  }
  async validateGrossWeightEditShipmentCA(index: number) {
    await this.$nextTick();

    if (!this.isShipmentCA) return;
    if (AccountController.accountData.isInternalAccount) return;

    this.totalPieces[index].errorGrossWeight = this
      .errorProhibitedChangeWeightShipmentCA
      ? this.$t("bookingV3.shipment.weightPieces.error.prohibitedChangeWeight")
      : "";
  }

  onValidateVolumeWeight(index: number) {
    const result = perItemSchema.safeParse({
      grossWeight: this.parseNumber(this.totalPieces[index].grossWeight),
      volumeWeight: this.parseNumber(this.totalPieces[index].volumeWeight)
    });

    this.totalPieces[index].errorVolumeWeight = result.success
      ? ""
      : this.$t(result.error.format().volumeWeight?._errors?.[0] || "");

    if (!this.totalPieces[index].errorVolumeWeight) {
      this.validateVolumeWeightEditShipmentCA(index);
    }
  }
  async validateVolumeWeightEditShipmentCA(index: number) {
    await this.$nextTick();

    if (!this.isShipmentCA) return;

    this.totalPieces[index].errorVolumeWeight = this
      .errorProhibitedChangeWeightShipmentCA
      ? this.$t("bookingV3.shipment.weightPieces.error.prohibitedChangeWeight")
      : "";
  }

  get errorIndex() {
    return this.currentIndex;
  }

  set errorIndex(value) {
    this.$emit("update:currentIndex", value);
  }

  get errorGrossWeight() {
    if (validationCurrentIndex(this.pieceIndex, this.errorIndex)) {
      return this.onValidateGrossWeight(this.pieceIndex);
    }
    return this.error.totalGrossWeight;
  }

  toggleTooltip(event: any) {
    const refs: any = this.$refs;
    refs["opTooltip"].toggle(event);
  }

  get isErrorHws() {
    return this.totalPieces.map((key: any) => key.errorHws).includes(true);
  }

  isAlreadyErrorMaxWeight(key: any) {
    return (
      (key.errorVolumeWeight.includes("400 kg/STT") &&
        !key.errorGrossWeight.includes("400 kg/STT")) ||
      (!key.errorVolumeWeight.includes("400 kg/STT") &&
        !key.errorGrossWeight.includes("400 kg/STT"))
    );
  }

  formatDimension(value: string) {
    return formatInputNoDecimal(value);
  }

  get isShipmentCA(): boolean {
    return isShipmentCa(this.form.shipmentId);
  }
  noEditableShipmentCa(index: number) {
    return this.isShipmentCA && index < this.piecesTemporary.length;
  }

  get isShowButtonDelete() {
    if (!this.isEditForm) return this.totalPieces.length > 1;
    return this.listKgDefault.length < this.pieces.length;
  }

  enableEditSizeBasedOnWeight(grossWeight: string) {
    const weight = this.parseNumber(grossWeight);
    const eligibleWeight = 10; // kgs
    return !this.eligibleEditSize || weight >= eligibleWeight;
  }

  // error ch weight max gap shipment MP (tokopedia)
  totalChargeableWeightKeeped = 0;
  get errorChWeightMaxGapShipmentMp(): boolean {
    const isError =
      this.validationPrefix(["T1", "TKLP", "TKP", "TSLP"]) &&
      gapValidationCHWeightTokped(
        this.totalChargeableWeightKeeped,
        this.totalChargeableWeight
      );
    CreateBookingV3Controller.setErrorChWeightMaxGapShipmentMp(isError);
    return isError;
  }

  validationPrefix(prefix: string[]): boolean {
    return prefix.some((e) => this.form.shipmentId.startsWith(e));
  }

  parseNumber(val: string) {
    return parseByCountry(val, {
      country: this.accountCountryCode,
    });
  }

  get districtDetail() {
    return LocationController.districtDetail;
  }

  get originCountry() {
    return (
      this.districtDetail?.countryCode ??
      AccountController.accountData.countryCode
    );
  }

  get isInterpack() {
    return ["INTERPACK", "INTERPACKMAXI"].includes(this.form.productName.toUpperCase())
  }

  get isMyPlatform(): boolean {
    return this.originCountry.toUpperCase() === "MY";
  }

  // Non-MY origin INTERPACK/INTERPACKMAXI stays capped at 1 koli (unchanged
  // pre-ticket behavior); only MY-origin lanes resolve real eligibility.
  get interpackLaneResolving(): boolean {
    return ProductController.interpackLaneResolving;
  }

  get interpackLaneEligible(): boolean {
    if (!this.isInterpack) return true;
    if (!this.isMyPlatform) return false;
    return ProductController.interpackLaneMultiplePieces;
  }
}

export default WeightKoli;
